import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Agnostic info badge ("i") with a styled tooltip popover.
 * Opens on hover (desktop) and click/tap (mobile/touch); closes on outside
 * click, Escape or any scroll. Reset typography (text-left / normal-case /
 * font-normal) so it can live inside uppercase or bold headers.
 *
 * The bubble renders in a portal on document.body, in fixed position: anchored
 * inside the card it was clipped by every `overflow` ancestor (a bubble near
 * the sidebar looked cut by it) and capped by local stacking contexts.
 *
 * Props:
 *  - text: tooltip content
 *  - className: extra classes on the wrapper (e.g. absolute positioning)
 *  - dotClassName: override the "i" dot styling (border/text color)
 *  - width: tooltip width utility (default 'w-56')
 */
const WIDTH_PX = { 'w-56': 224, 'w-64': 256, 'w-72': 288, 'w-80': 320 };

const InfoTip = ({ text, className = '', dotClassName = '', width = 'w-56' }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);

  const show = () => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const w = WIDTH_PX[width] || 224;
    // Below the dot, right edges aligned — clamped inside the viewport.
    const left = Math.max(8, Math.min(rect.right - w, window.innerWidth - w - 8));
    setPos({ top: rect.bottom + 6, left });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // A fixed bubble does not follow its anchor: any scroll closes it.
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  if (!text) return null;

  return (
    <span
      ref={ref}
      className={`relative inline-flex ${className}`}
      onMouseEnter={show}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="Plus d'informations"
        onClick={(e) => { e.stopPropagation(); if (open) setOpen(false); else show(); }}
        className={`inline-flex w-3.5 h-3.5 rounded-full border items-center justify-center text-[9px] font-bold leading-none cursor-help outline-none transition-colors ${
          dotClassName || 'border-gray-300 dark:border-gray-600 text-gray-400 hover:border-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
        }`}
      >
        i
      </button>
      {open && pos && createPortal(
        <span
          role="tooltip"
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 90 }}
          className={`${width} block rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-[11px] font-normal normal-case tracking-normal leading-snug text-gray-600 dark:text-gray-300 shadow-xl text-left`}
        >
          {text}
        </span>,
        document.body,
      )}
    </span>
  );
};

export default InfoTip;
